/*
 * The preview's made-up server. It answers the same routes, with the same shapes, permission table and error messages as
 * the real server, from data kept in the browser (the same fake people and companies as `npm run seed`, plus The Washers
 * so all three brand colors can be seen). Nothing is saved; a reload starts over. Only the preview build uses this.
 */
import {
  lookFor,
  can,
  canGiveRole,
  companyLevel,
  keyProvidersAt,
  LOCATION_LOCKED_FIELDS,
  resolveSettings,
  settingsPatchSchema,
  shortName,
  OPEN_WHEN_NAMES_PRIVATE,
  readClientSheet,
  TWO_STEP_ROLES,
  type KeyProvider,
  type Role,
} from "@steward/shared";
import {
  moneyPublicRoute,
  moneyRoute,
  proposalsRoute,
  quoteFilePicture,
  seedMoney,
  type MoneyBrand,
  type MoneyCtx,
} from "./money";
import { calendarProblem, payrollRoute, seedPayroll, setShowsMyPay, showsMyPay } from "./payroll";
import { homeAnswer, seedWork, workCounts, workFilePicture, workRoute } from "./work";
import { automationsRoute, marketingRoute, websiteRoute } from "./automations";
import { fieldRoute, inboxRoute, myRoute } from "./field";
import { hubQuoteRoute } from "./hub-quote";
import { booksRoute, insightsRoute } from "./books";
import { hqRoute, namesPrivate, seedHq } from "./hq";
import { suiteRoute } from "./suite";
import { campaignsRoute } from "./campaigns";
import { marketingTabsRoute } from "./marketing-tabs";
import { agencyRoute, seedAgency } from "./agency";

type Json = Record<string, unknown>;
interface Co {
  id: string;
  parentId: string | null;
  name: string;
  slug: string;
  trade: string;
  status: "active" | "paused";
  timezone: string;
  brandColor: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  website: string | null;
  ctaColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  logoUrl?: string | null;
  logoWhiteUrl?: string | null;
  businessType: "home" | "business";
  settings: Json;
}
interface User {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  locale: "en" | "es";
  password: boolean;
  totp: boolean;
  roles: Record<string, Role>;
}
interface Person {
  id: string;
  companyId: string;
  userId: string | null;
  firstName: string;
  lastName: string;
  role: string;
  seesPrices: boolean;
  countsOnSchedule?: boolean;
  active: boolean;
  locale: string;
  color?: string | null;
  /** A profile picture: its made-up file id (the picture itself is in pictures). */
  photo?: string | null;
}

/** Profile pictures uploaded in the preview, as data links by their path (people/<id>/photo/<file id>). */
const pictures = new Map<string, string>();
/** A preview profile picture, for pictureUrl in api/client.ts. */
export const previewPicture = (path: string): string | null =>
  pictures.get(path) ?? workFilePicture(path) ?? quoteFilePicture(path);

let n = 0;
const id = () => `00000000-0000-4000-8000-${String(++n).padStart(12, "0")}`;
const now = () => new Date().toISOString();

/** Settings > Recently deleted in the preview: one archived request and one removed note. */
const demoDeleted: {
  kind: "note" | "request";
  id: string;
  title: string;
  on: "request";
  onId: string;
  deletedAt: string;
}[] = [
  {
    kind: "request",
    id: "00000000-0000-4000-9000-000000000001",
    title: "Mia Torres",
    on: "request",
    onId: "00000000-0000-4000-9000-000000000001",
    deletedAt: new Date(Date.now() - 2 * 86_400_000).toISOString(),
  },
  {
    kind: "note",
    id: "00000000-0000-4000-9000-000000000002",
    title: "Back gate sticks, lift it up",
    on: "request",
    onId: "00000000-0000-4000-9000-000000000001",
    deletedAt: new Date(Date.now() - 5 * 3_600_000).toISOString(),
  },
];
const minsAgo = (m: number) => new Date(Date.now() - m * 60_000).toISOString();

const co = (
  name: string,
  slug: string,
  trade: string,
  parentId: string | null,
  brandColor: string | null,
  extra: Partial<Co> = {},
): Co => ({
  id: id(),
  parentId,
  name,
  slug,
  trade,
  status: "active",
  timezone: "America/Chicago",
  brandColor,
  phone: null,
  email: null,
  address: null,
  website: null,
  businessType: "home",
  settings: {},
  ...extra,
});

const hq = co("The Trades", "the-trades", "hq", null, "#1D3A4A", { phone: "(504) 555-0100", email: "office@example.com" });
/** A made-up price list for the preview's auto quoter. Not The Cleaners' real prices. */
const DEMO_PRICES = {
  baseRooms: 2,
  services: [
    { id: "s1", name: "Standard Cleaning", perRoomCents: 2000, perSqftCents: 8, minCents: 12000, repeats: true },
    { id: "s2", name: "Deep Cleaning", perRoomCents: 3200, perSqftCents: 13, minCents: 20000, repeats: true },
    { id: "s3", name: "Move Out Cleaning", perRoomCents: 3800, perSqftCents: null, minCents: 25000, repeats: false },
  ],
  repeat: {
    weekly: { perRoomCents: 1500, perSqftCents: null, minCents: 10000 },
    biweekly: { perRoomCents: 1700, perSqftCents: null, minCents: 11000 },
    monthly: { perRoomCents: 2000, perSqftCents: null, minCents: 12000 },
  },
  extras: [
    { id: "x1", name: "Inside the fridge", priceCents: 3500, planCents: 2500, each: false, only: null },
    { id: "x2", name: "Inside the oven", priceCents: 3500, planCents: 2500, each: false, only: null },
    { id: "x3", name: "Inside windows", priceCents: 500, each: true, only: null },
    { id: "x4", name: "Inside cabinets", priceCents: 4000, each: false, only: "s3" },
  ],
};
const cleaners = co("The Cleaners", "the-cleaners", "cleaning", hq.id, "#0B4F55", {
  settings: { invoices: { pastDueHoursHome: 48 }, prices: DEMO_PRICES },
});
const cleanersNola = co("The Cleaners of New Orleans", "the-cleaners-1", "cleaning", cleaners.id, null, {
  phone: "(504) 555-0110",
  address: "100 Example St, New Orleans, LA 70115",
  email: "neworleans@example.com",
});
const groundsmen = co("The GroundsMen", "the-groundsmen", "lawn", hq.id, "#173F28");
const groundsmenNola = co("The GroundsMen of New Orleans", "the-groundsmen-1", "lawn", groundsmen.id, null);
const washers = co("The Washers", "the-washers", "washers", hq.id, "#0B2E63");
const washersNola = co("The Washers of New Orleans", "the-washers-1", "washers", washers.id, null, {
  businessType: "business",
});
const cleanersMetairie = co("The Cleaners of Metairie", "the-cleaners-2", "cleaning", cleaners.id, null);
/** The Marketers, the agency (draft 13): run by head office, so the owner works in it. */
const marketers = co("The Marketers", "the-marketers", "agency", hq.id, "#111111", { email: "hello@example.com" });
const companies: Co[] = [
  hq,
  cleaners,
  cleanersNola,
  cleanersMetairie,
  groundsmen,
  groundsmenNola,
  washers,
  washersNola,
  marketers,
];
// Draft 11: The Cleaners of New Orleans plays the franchise (Faye Owens); the rest are run by head office.
seedHq(cleanersNola.id, {
  [cleanersNola.id]: ["70112", "70113", "70114", "70115", "70116", "70117", "70118", "70119", "70130"],
  [cleanersMetairie.id]: ["70001", "70002", "70003", "70005", "70006"],
  [groundsmenNola.id]: ["70115", "70118", "70124", "70125"],
  [washersNola.id]: ["70115", "70130"],
});
/** Where the preview opens for every role: the location with the made-up team. */
export const PREVIEW_HOME = cleanersNola.id;

const user = (
  name: string,
  email: string | null,
  phone: string | null,
  locale: "en" | "es",
  roles: Record<string, Role>,
): User => ({
  id: id(),
  name,
  email,
  phone,
  locale,
  password: Boolean(email),
  totp: false,
  roles,
});
const owner = user("Olivia Grant", "owner@example.com", "+15045550100", "en", { [hq.id]: "owner" });
const manager = user("Marcus Bell", "manager@example.com", "+15045550101", "en", { [cleanersNola.id]: "manager" });
const dispatch = user("Dana Ruiz", "dispatch@example.com", "+15045550102", "en", { [cleanersNola.id]: "dispatch" });
const kenia = user("Kenia Ramos", null, "+15045550103", "es", { [cleanersNola.id]: "crew" });
const kevin = user("Kevin Rhodes", null, "+15045550104", "en", { [cleanersNola.id]: "crew" });
/** A franchise owner of the New Orleans location only, to see what a location can and can't change itself. */
const franchise = user("Faye Owens", "franchise@example.com", "+15045550105", "en", { [cleanersNola.id]: "franchise_owner" });
manager.totp = true;
const users = [owner, manager, dispatch, kenia, kevin, franchise];
seedAgency({
  companyId: marketers.id,
  team: [
    { id: owner.id, name: owner.name },
    { id: id(), name: "Jun Reyes" },
  ],
  ours: [cleanersNola, cleanersMetairie, groundsmenNola, washersNola].map((x) => ({ id: x.id, name: x.name })),
});

const person = (
  c: Co,
  u: User | null,
  firstName: string,
  lastName: string,
  role: string,
  extra: Partial<Person> = {},
): Person => ({
  id: id(),
  companyId: c.id,
  userId: u?.id ?? null,
  firstName,
  lastName,
  role,
  seesPrices: false,
  active: true,
  locale: u?.locale ?? "en",
  ...extra,
});
const people: Person[] = [
  // The owner works visits too, so she is on the team list and has My pay (question 49).
  person(cleanersNola, owner, "Olivia", "Grant", "owner"),
  person(cleanersNola, manager, "Marcus", "Bell", "manager"),
  person(cleanersNola, dispatch, "Dana", "Ruiz", "dispatch"),
  person(cleanersNola, kenia, "Kenia", "Ramos", "crew"),
  person(cleanersNola, kevin, "Kevin", "Rhodes", "crew"),
  person(cleanersNola, null, "Rosa", "Diaz", "crew", { locale: "es" }),
  person(cleanersNola, null, "Kevin", "Rios", "crew", { active: false }),
  person(groundsmenNola, null, "Luis", "Moreno", "crew"),
  person(groundsmenNola, null, "Jake", "Turner", "crew"),
];

/** New Orleans' date today: jobs are dated around it so Home and the crew's day have visits. */
const TODAY = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date());

seedWork({
  id,
  today: TODAY,
  people,
  cleaners: cleanersNola.id,
  lawn: groundsmenNola.id,
  washers: washersNola.id,
});

interface Key {
  companyId: string;
  provider: string;
  preview: string;
  createdAt: string;
}
const keys: Key[] = [{ companyId: hq.id, provider: "quo", preview: "quo-••••", createdAt: minsAgo(60 * 24 * 3) }];

interface Msg {
  companyId: string;
  id: string;
  channel: string;
  purpose: string;
  to: string;
  subject: string | null;
  body: string;
  template: string;
  status: string;
  sendAfter: string;
  error: string | null;
  createdAt: string;
}
const outbox: Msg[] = [];
const msg = (c: Co, m: Partial<Msg> & { to: string; template: string; channel: string; status: string }, at: string) =>
  outbox.unshift({
    companyId: c.id,
    id: id(),
    purpose: "transactional",
    subject: null,
    body: "",
    error: null,
    sendAfter: at,
    createdAt: at,
    ...m,
  });
msg(
  cleanersNola,
  {
    channel: "sms",
    to: "+15045550170",
    template: "reminder",
    status: "held",
    body: "Hi Sam, your cleaning is tomorrow at 9 AM.",
  },
  minsAgo(50),
);
msg(
  cleanersNola,
  {
    channel: "sms",
    to: "+15045550172",
    template: "reminder",
    status: "cancelled",
    error: "opted_out_stop",
    body: "Hi Jo, your office cleaning is Friday.",
  },
  minsAgo(180),
);
msg(
  cleanersNola,
  { channel: "sms", to: "+15045550149", template: "invite", status: "dry_run", body: "Invite link (hidden)" },
  minsAgo(60 * 26),
);
msg(
  cleanersNola,
  { channel: "sms", to: "+15045550103", template: "sign_in_code", status: "dry_run", body: "Sign-in code (hidden)" },
  minsAgo(60 * 5),
);

interface Audit {
  companyId: string;
  id: string;
  action: string;
  userId: string | null;
  userName: string | null;
  subjectType: string | null;
  subjectId: string | null;
  ip: string | null;
  details: Json;
  createdAt: string;
}
const audit: Audit[] = [];
const log = (c: Co, u: User | null, action: string, at = now()) =>
  audit.unshift({
    companyId: c.id,
    id: id(),
    action,
    userId: u?.id ?? null,
    userName: u?.name ?? null,
    subjectType: null,
    subjectId: null,
    ip: "10.0.0.24",
    details: {},
    createdAt: at,
  });
log(cleanersNola, manager, "people.invite", minsAgo(60 * 26));
log(cleanersNola, kenia, "auth.sign_in", minsAgo(60 * 5));
log(cleanersNola, manager, "auth.sign_in", minsAgo(60 * 2));
log(cleanersNola, dispatch, "auth.sign_in", minsAgo(40));

/** Who the preview is signed in as, and whether their two-step is done. */
let session: { userId: string; twoStepDone: boolean; started: string } | null = null;
const otherSessions = [
  {
    id: id(),
    kind: "mobile",
    ip: "10.0.0.31",
    userAgent: null,
    createdAt: minsAgo(60 * 24 * 6),
    lastUsedAt: minsAgo(60 * 9),
    current: false,
  },
  {
    id: id(),
    kind: "web",
    ip: "10.0.0.24",
    userAgent: "Mozilla/5.0 (Windows NT 10.0) Chrome/130",
    createdAt: minsAgo(60 * 24 * 2),
    lastUsedAt: minsAgo(60 * 20),
    current: false,
  },
];

const ok = (body: unknown, status = 200) => Promise.resolve({ status, body });
const fail = (status: number, code: string, message: string) => Promise.resolve({ status, body: { code, message } });
const NOT_ALLOWED = () => fail(403, "not_allowed", "Your role can't do that. Ask the owner if you need it.");

const chain = (c: Co): Co[] => {
  const out: Co[] = [];
  let at: Co | undefined = c;
  while (at) {
    out.unshift(at);
    const parent: string | null = at.parentId;
    at = parent ? companies.find((x) => x.id === parent) : undefined;
  }
  return out;
};
const below = (cid: string): Co[] => companies.filter((c) => c.parentId === cid).flatMap((c) => [c, ...below(c.id)]);
const roleIn = (u: User, c: Co): Role | null =>
  u.roles[c.id] ??
  (chain(c)
    .slice(0, -1)
    .some((a) => u.roles[a.id] === "owner")
    ? "owner"
    : null);
/** Brands carry the color; a location takes the nearest one above it. */
const companyAnswer = (c: Co, role: Role | null, u?: User) => {
  const { settings: _s, ...rest } = c;
  const near = (k: "brandColor" | "ctaColor" | "secondaryColor" | "accentColor") =>
    c[k] ?? [...chain(c)].reverse().find((x) => x[k])?.[k] ?? null;
  return {
    ...rest,
    brandColor: near("brandColor"),
    ctaColor: near("ctaColor"),
    secondaryColor: near("secondaryColor"),
    accentColor: near("accentColor"),
    role,
    level: companyLevel(chain(c)),
    namesPrivate: u ? namesPrivate(c.id, !u.roles[c.id]) : false,
    // The preview has one address, so every company's links live where it runs.
    appUrl: null,
    linkUrl: window.location.origin,
    clientUrl: null,
    clientLinkUrl: window.location.origin,
    logoUrl: c.logoUrl ?? null,
    logoWhiteUrl: c.logoWhiteUrl ?? null,
  };
};
/** What the money pages need about a company. The preview acts as if Stripe test mode is set up, so the pay page works. */
const brandOf = (companyId: string): MoneyBrand => {
  const c = companies.find((x) => x.id === companyId) ?? cleanersNola;
  const inv = resolveSettings(chain(c).map((x) => x.settings)).invoices;
  return {
    name: c.name,
    ...(({ brandColor, ctaColor, secondaryColor, accentColor, logoUrl, logoWhiteUrl }) => ({
      color: brandColor,
      ctaColor,
      secondaryColor,
      accentColor,
      logoUrl,
      logoWhiteUrl,
    }))(companyAnswer(c, null)),
    phone: c.phone,
    email: c.email,
    trade: c.trade,
    timezone: c.timezone,
    tips: inv.tips,
    invoiceText: inv.text,
    invoiceSubject: inv.subject,
    pastDueHoursHome: inv.pastDueHoursHome,
    pastDueDaysBusiness: inv.pastDueDaysBusiness,
    stripe: true,
  };
};
const handCrew = people.find((p) => p.companyId === cleanersNola.id && p.role === "crew" && p.active);
seedMoney({
  id,
  today: TODAY,
  companyId: cleanersNola.id,
  brand: brandOf(cleanersNola.id),
  handCrew: handCrew ? { id: handCrew.id, name: shortName(handCrew.firstName, handCrew.lastName) } : null,
});
seedPayroll({ id, today: TODAY, companyId: cleanersNola.id, people });

const companiesFor = (u: User) => {
  const out = new Map<string, { c: Co; role: Role }>();
  for (const [cid, role] of Object.entries(u.roles)) {
    const c = companies.find((x) => x.id === cid);
    if (!c) continue;
    out.set(c.id, { c, role });
    if (role === "owner") for (const k of below(c.id)) if (!out.has(k.id)) out.set(k.id, { c: k, role: "owner" });
  }
  return [...out.values()].sort((a, b) => a.c.name.localeCompare(b.c.name));
};
const personRow = (p: Person) => ({
  id: p.id,
  name: shortName(p.firstName, p.lastName),
  firstName: p.firstName,
  lastName: p.lastName,
  role: p.role,
  hasLogin: p.userId !== null,
  seesPrices: p.seesPrices,
  showsMyPay: showsMyPay(p.id),
  countsOnSchedule: p.countsOnSchedule ?? p.role === "crew",
  active: p.active,
  locale: p.locale,
  color: p.color ?? null,
  photo: p.photo ?? null,
});
const needsTwoStep = (u: User) => u.totp || Object.values(u.roles).some((r) => TWO_STEP_ROLES.includes(r));

/** Signs the preview in as one of the made-up people, skipping the sign-in steps. */
export function previewSignInAs(who: "owner" | "franchise" | "manager" | "dispatch" | "crew"): void {
  const u = { owner, franchise, manager, dispatch, crew: kenia }[who];
  if (TWO_STEP_ROLES.some((r) => Object.values(u.roles).includes(r))) u.totp = true;
  session = { userId: u.id, twoStepDone: true, started: now() };
  log(companies.find((c) => u.roles[c.id]) ?? hq, u, "auth.sign_in");
}
export function previewWho(): string | null {
  const u = users.find((x) => x.id === session?.userId);
  if (!u) return null;
  if (u === franchise) return "franchise";
  return u === owner ? "owner" : u === manager ? "manager" : u === dispatch ? "dispatch" : "crew";
}

export async function previewServer(
  method: string,
  path: string,
  headers: Record<string, string>,
  body: unknown,
): Promise<{ status: number; body: unknown }> {
  await new Promise((r) => setTimeout(r, 120));
  const url = new URL(path, "http://preview");
  const p = url.pathname.replace(/^\/v1\//, "");
  const b = (body ?? {}) as Json;
  const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
  const route = `${method} ${p}`;

  // Public routes
  if (route === "POST auth/sign-in") {
    const u = users.find((x) => x.email === str(b.email).toLowerCase());
    if (!u || !u.password || str(b.password).length < 1)
      return fail(401, "sign_in_wrong", "That email or password is not right. Check them and try again.");
    const need = needsTwoStep(u);
    session = { userId: u.id, twoStepDone: !need, started: now() };
    log(companies.find((c) => u.roles[c.id]) ?? hq, u, "auth.sign_in");
    return ok({ twoStep: !need ? "done" : u.totp ? "needed" : "setup" });
  }
  if (route === "POST auth/code") return ok({ ok: true });
  if (route === "POST auth/code/verify") {
    const phone = str(b.phone).replace(/[^\d]/g, "").slice(-10);
    const u = users.find((x) => x.phone?.endsWith(phone) && !x.password);
    if (!u || b.code !== "123456")
      return fail(
        401,
        "code_wrong",
        "That code or phone number is not right, or the code has run out. Check them, or ask for a new code.",
      );
    session = { userId: u.id, twoStepDone: true, started: now() };
    return ok({ twoStep: "done" });
  }
  if (method === "GET" && p.startsWith("public/invites/"))
    return ok({ companyName: cleanersNola.name, role: "crew", firstName: "Rosa", by: "phone" });
  if (method === "POST" && p.startsWith("public/invites/")) return ok({ signIn: "code" });
  if (route === "GET health") return ok({ ok: true, database: true });
  if (route === "GET public/sign-in-look") return ok({ brand: null });
  const hub = hubQuoteRoute(
    method,
    p,
    body,
    DEMO_PRICES,
    () => moneyPublicRoute("GET", "public/quote/demo", undefined, brandOf) ?? ok({}),
  );
  if (hub) return hub;
  // The office's preview of a quote page (api quotes/:id/preview): the made-up quote page.
  if (method === "GET" && /^quotes\/[^/]+\/preview$/.test(p))
    return moneyPublicRoute("GET", "public/quote/demo", undefined, brandOf) ?? ok({});
  // The review page (Public.tsx ReviewPage): /r/demo shows it with a crew of two and the brand's Google rating.
  if (/^public\/review\/[^/]+$/.test(p)) {
    if (method === "POST")
      return ok({ go: Number((body as { stars?: number } | undefined)?.stars) >= 4 ? "https://www.google.com/" : null });
    const b = brandOf(cleanersNola.id);
    return ok({
      business: b.name,
      brandColor: b.color,
      look: lookFor("cleaning", b.logoWhiteUrl ?? b.logoUrl, { accent: b.accentColor, menu: b.secondaryColor, cta: b.ctaColor }),
      firstName: "Norah",
      job: "deep clean",
      day: new Date(Date.now() - 86_400_000).toISOString().slice(0, 10),
      crew: [
        { id: "00000000-0000-4000-8000-0000000000c1", first: "Kenia", photo: false },
        { id: "00000000-0000-4000-8000-0000000000c2", first: "Viviana", photo: false },
      ],
      google: { rating: 4.8, reviews: 110 },
      trade: "cleaning",
    });
  }
  const pub = moneyPublicRoute(method, p, body, brandOf);
  if (pub) return pub;
  const mine = myRoute(method, p, body);
  if (mine) return mine;

  // Signed in
  const me = users.find((x) => x.id === session?.userId);
  if (!me || !session) return fail(401, "not_signed_in", "Please sign in again.");
  const before = [
    "POST auth/two-step",
    "POST auth/two-step/setup",
    "POST auth/two-step/confirm",
    "POST auth/sign-out",
    "GET auth/me",
  ];
  if (!session.twoStepDone && !before.includes(route)) {
    return me.totp
      ? fail(403, "two_step_needed", "Type the code from your authenticator app to finish signing in.")
      : fail(403, "two_step_setup_needed", "Set up two-step sign in to keep going.");
  }
  const sixDigits = () => /^\d{6}$/.test(str(b.code)) || /^[a-z0-9]{4}-?[a-z0-9]{4}$/i.test(str(b.code));
  switch (route) {
    case "GET auth/me":
      return ok({
        id: me.id,
        name: me.name,
        email: me.email,
        phone: me.phone,
        locale: me.locale,
        twoStepOn: me.totp,
        companies: companiesFor(me).map(({ c, role }) => ({ id: c.id, name: c.name, role })),
      });
    case "POST auth/two-step":
      if (!sixDigits())
        return fail(401, "two_step_wrong", "That code is not right. Type the 6 digits your app shows now, or a backup code.");
      session.twoStepDone = true;
      return ok({ ok: true });
    case "POST auth/two-step/setup":
      return ok({
        secret: "JBSWY3DPEHPK3PXPJBSWY3DPEHPK3PXP",
        otpauthUrl: `otpauth://totp/Steward:${encodeURIComponent(me.email ?? "")}?secret=JBSWY3DPEHPK3PXPJBSWY3DPEHPK3PXP&issuer=Steward`,
      });
    case "POST auth/two-step/confirm":
      if (!sixDigits()) return fail(401, "two_step_wrong", "That code is not right. Type the 6 digits your app shows now.");
      me.totp = true;
      session.twoStepDone = true;
      return ok({
        backupCodes: [
          "4k7q-9m2x",
          "h3pt-c8vw",
          "q2mn-7rds",
          "x9kf-3bty",
          "w6jh-p4nc",
          "t8gz-5mqe",
          "b3vu-k7wa",
          "n5rc-2hxj",
          "f7ys-6dkp",
          "m4ea-9tlg",
        ],
      });
    case "POST auth/language":
      me.locale = str(b.locale) === "es" ? "es" : "en";
      return ok({ ok: true });
    case "POST auth/sign-out":
    case "POST auth/sign-out-everywhere":
      session = null;
      return ok(route.endsWith("everywhere") ? { ended: 3 } : { ok: true });
    case "GET auth/sessions":
      return ok([
        {
          id: "this",
          kind: "web",
          ip: "10.0.0.24",
          userAgent: navigator.userAgent,
          createdAt: session.started,
          lastUsedAt: now(),
          current: true,
        },
        ...otherSessions,
      ]);
    case "GET auth/claude/client":
      return ok({ name: "Claude", redirectHost: "claude.ai" });
    case "POST auth/claude/approve":
      return ok({ redirect: "/settings/claude" });
    case "GET landing":
      return ok({ kind: "open", companyId: null });
    case "GET companies":
      return ok(companiesFor(me).map(({ c, role }) => companyAnswer(c, role, me)));
  }
  if (method === "DELETE" && p.startsWith("auth/sessions/")) {
    const i = otherSessions.findIndex((s) => s.id === p.split("/")[2]);
    if (i < 0) return fail(404, "not_found", "That session was not found.");
    otherSessions.splice(i, 1);
    return ok({ ok: true });
  }

  // Company routes
  const c = companies.find((x) => x.id === headers["x-company-id"]);
  if (!c) return fail(403, "no_company", "Pick a company first.");
  const role = roleIn(me, c);
  if (!role) return fail(403, "not_your_company", "You don't have access to that company.");
  // A franchise that keeps its customer names private: owners from above get only the business parts (draft 11).
  const priv = namesPrivate(c.id, !me.roles[c.id]);
  const gate = (action: Parameters<typeof can>[1]) => can(role, action) && (!priv || OPEN_WHEN_NAMES_PRIVATE.includes(action));
  const moneyCtx = (x: Co, r: Role, u: User): MoneyCtx => ({
    companyId: x.id,
    me: u.name,
    isOwner: r === "owner",
    gate,
    brand: brandOf(x.id),
    people: people.filter((y) => y.companyId === x.id),
    queue: (m) => msg(x, { ...m, status: "dry_run" }, now()),
    log: (a) => log(x, u, a),
  });

  if (route === "GET company") return gate("company.read") ? ok(companyAnswer(c, role, me)) : NOT_ALLOWED();
  if (route === "POST company/logo") {
    if (!gate("company.update")) return NOT_ALLOWED();
    if (!["image/png", "image/jpeg", "image/webp"].includes(str(b.mime)))
      return fail(400, "bad_request", "Use a PNG, JPG or WebP picture.");
    // The preview keeps the picture in the page; the real server saves it and links to it.
    const url = `data:${str(b.mime)};base64,${str(b.dataBase64)}`;
    if (b.variant === "white") c.logoWhiteUrl = url;
    else c.logoUrl = url;
    return ok(companyAnswer(c, role, me));
  }
  if (method === "PATCH" && p.startsWith("companies/")) {
    if (!gate("company.update")) return NOT_ALLOWED();
    const target = companies.find((x) => x.id === p.split("/")[1]);
    if (!target || (target !== c && !below(c.id).includes(target))) return fail(404, "not_found", "That company was not found.");
    if (b.brandColor !== undefined && !/^#[0-9a-f]{6}$/i.test(str(b.brandColor)))
      return fail(400, "bad_request", "Check brandColor. Use a color like #1F6F5C.");
    for (const k of ["ctaColor", "secondaryColor", "accentColor"] as const)
      if (b[k] !== undefined && b[k] !== null && !/^#[0-9a-f]{6}$/i.test(str(b[k])))
        return fail(400, "bad_request", `Check ${k}. Use a color like #1F6F5C.`);
    if (b.name !== undefined && !str(b.name).trim()) return fail(400, "bad_request", "Check name. Type the business name.");
    // Same rule as the server: a location's name and phone are the brand's to change (an owner above it still can).
    const lockedChange = LOCATION_LOCKED_FIELDS.some((f) => b[f] !== undefined && b[f] !== target[f]);
    const ownsAbove = chain(target)
      .slice(0, -1)
      .some((a) => me.roles[a.id] === "owner");
    if (lockedChange && companyLevel(chain(target)) === "location" && !ownsAbove)
      return fail(
        403,
        "location_info_locked",
        "A location can't change its own name or phone. Ask the brand or head office to change it.",
      );
    Object.assign(target, b);
    log(c, me, "company.update");
    return ok(companyAnswer(target, role));
  }
  const autos = automationsRoute(c.id, role === "owner" || role === "franchise_owner", gate("settings.read"), method, p, b);
  if (autos) return autos;
  const web = websiteRoute(c.id, gate("settings.read"), gate("settings.update"), method, p, b);
  if (web) return web;
  const mk = marketingRoute(gate("client.update"), method, p, b);
  if (mk) return mk;
  const camp = campaignsRoute(gate, c, method, p, b);
  if (camp) return camp;
  const tabs = marketingTabsRoute(gate, c, method, p, url.searchParams, b);
  if (tabs) return tabs;
  const suite = suiteRoute(gate, c, method, p, b);
  if (suite) return suite;
  // A proposal from a deal is a quote with a monthly line (the quotes part answers it, as on the server).
  if (method === "POST" && /^agency\/deals\/[^/]+\/proposal$/.test(p)) {
    if (c.trade !== "agency") return fail(404, "not_agency", "This page is only for the marketing agency.");
    const lead = p.split("/")[2] ?? "";
    const monthly = typeof b.monthlyCents === "number" ? b.monthlyCents : 0;
    const setup = typeof b.setupCents === "number" ? b.setupCents : 0;
    const line = (description: string, cents: number, bill: "once" | "monthly") => ({
      description,
      quantity: 1,
      unitPriceCents: cents,
      optional: false,
      picked: true,
      recurring: false,
      bill,
    });
    const made = await moneyRoute(moneyCtx(c, role, me), "POST", "quotes", url.searchParams, {
      leadId: lead,
      contactName: "New proposal",
      title: "Marketing plan",
      lines: [
        line(str(b.description, "Monthly marketing"), monthly, "monthly"),
        ...(Array.isArray(b.extras) ? (b.extras as { description: string; monthlyCents: number }[]) : []).map((x) =>
          line(x.description, x.monthlyCents, "monthly"),
        ),
        ...(setup ? [line("Setup", setup, "once")] : []),
      ],
    });
    if (!made) return fail(404, "not_found", "That deal was not found.");
    return made.status === 201 ? ok({ quoteId: (made.body as { id: string }).id }, 201) : made;
  }
  // Draft 14: the client import (checked by the same shared reader; nothing is saved in the preview) and view as.
  if (method === "POST" && (p === "import/clients/check" || p === "import/clients")) {
    if (!gate("client.bulk")) return NOT_ALLOWED();
    const sheet = readClientSheet(str(b.csv), "2999-12-31");
    const rows = sheet.rows.map((r) => ({
      line: r.line,
      name: r.client?.name ?? null,
      action: !r.client || r.problems.some((x) => x.startsWith("needs")) ? "skip" : "add",
      clientId: null,
      problems: r.problems,
    }));
    return ok({
      columns: sheet.columns,
      rows,
      add: rows.filter((r) => r.action === "add").length,
      update: 0,
      skip: rows.filter((r) => r.action === "skip").length,
    });
  }
  if (method === "GET" && /^team\/[^/]+\/day$/.test(p)) {
    if (!gate("security.read")) return NOT_ALLOWED();
    const person = people.find((x) => x.id === p.split("/")[1] && x.companyId === c.id);
    if (!person) return fail(404, "not_found", "That person was not found.");
    const name = [person.firstName, person.lastName].filter(Boolean).join(" ");
    const day = await workRoute(
      { companyId: c.id, me: { id: person.userId ?? "", name }, role: "crew", gate: () => true, people, log: () => undefined },
      "GET",
      "my-day",
      url.searchParams,
      {},
    );
    const visits = ((day?.body ?? []) as Record<string, unknown>[]).map((v) => ({
      ...v,
      priceCents: person.seesPrices ? v.priceCents : 0,
    }));
    return ok({ name, visits });
  }
  const ag = agencyRoute(gate, c, me.id, method, p, url.searchParams, b, (name) => {
    const made = co(name, `${c.slug}-${companies.length + 1}`, "client", c.id, null, { businessType: "business" });
    companies.push(made);
    return made;
  });
  if (ag) return ag;
  const fd = fieldRoute(gate, c.id, method, p, url.searchParams, b);
  if (fd) return fd;
  if (p === "recently-deleted") return ok({ items: demoDeleted });
  const back = /^(notes|files|requests)\/([^/]+)\/restore$/.exec(p);
  if (back && demoDeleted.some((x) => x.id === back[2])) {
    demoDeleted.splice(
      demoDeleted.findIndex((x) => x.id === back[2]),
      1,
    );
    return ok({});
  }
  const ib = inboxRoute(method, p, b, url.searchParams);
  if (ib) return ib;
  const ins = insightsRoute(gate, [c, ...below(c.id)], method, p, url.searchParams, b);
  if (ins) return ins;
  const bk = booksRoute(gate, method, p, url.searchParams, b);
  if (bk) return bk;
  const ho = hqRoute(
    {
      c,
      role,
      me,
      companies,
      users,
      audit,
      below,
      levelOf: (x) => companyLevel(chain(x as Co)),
      gate,
      log: (x, action, details = {}) => {
        log(x as Co, me, action);
        audit[0]!.details = details;
      },
    },
    method,
    p,
    url.searchParams,
    b,
  );
  if (ho) return ho;
  if (route === "GET settings") {
    if (!gate("settings.read")) return NOT_ALLOWED();
    return ok({ settings: resolveSettings(chain(c).map((x) => x.settings)), own: c.settings });
  }
  if (route === "PATCH settings") {
    if (!gate("settings.update")) return NOT_ALLOWED();
    const parsed = settingsPatchSchema.safeParse(b);
    if (!parsed.success) return fail(400, "bad_request", "Something in the request is not right.");
    if (parsed.data.messaging?.live)
      return fail(
        409,
        "messaging_locked",
        "Real texts and emails are turned off in this version. Nothing is sent to clients yet.",
      );
    const pay = parsed.data.payroll;
    if (pay && (pay.periodWeeks !== undefined || pay.twoWeekFrom !== undefined)) {
      const now = resolveSettings(chain(c).map((x) => x.settings)).payroll;
      const problem = calendarProblem(
        c.id,
        now,
        pay.periodWeeks ?? now.periodWeeks,
        pay.twoWeekFrom !== undefined ? pay.twoWeekFrom : now.twoWeekFrom,
      );
      if (problem) return fail(409, problem.code, problem.message);
    }
    const merged: Json = { ...c.settings };
    for (const [k, v] of Object.entries(parsed.data)) merged[k] = { ...(merged[k] ?? {}), ...(v as Json) };
    c.settings = merged;
    log(c, me, "settings.update");
    return ok({ settings: resolveSettings(chain(c).map((x) => x.settings)), own: c.settings });
  }
  const myRow = people.find((x) => x.companyId === c.id && x.userId === me.id);
  const myAnswer = (x: Person) => ({
    id: x.id,
    name: shortName(x.firstName, x.lastName),
    color: x.color ?? null,
    photo: x.photo ?? null,
  });
  if (p === "me/person" || p === "me/person/photo") {
    if (!myRow) return fail(404, "not_on_team", "You are not on this company's team, so there is no picture to change.");
    if (method === "PATCH") myRow.color = typeof b.color === "string" ? b.color : null;
    if (method === "DELETE") myRow.photo = null;
    if (method === "POST") {
      myRow.photo = id();
      pictures.set(`people/${myRow.id}/photo/${myRow.photo}`, `data:${str(b.mime)};base64,${str(b.dataBase64)}`);
    }
    return ok(myAnswer(myRow));
  }
  if (route === "GET people")
    return gate("people.read") ? ok(people.filter((x) => x.companyId === c.id).map(personRow)) : NOT_ALLOWED();
  if (method === "PUT" && /^people\/[^/]+\/role$/.test(p)) {
    if (!gate("people.update_roles")) return NOT_ALLOWED();
    const target = people.find((x) => x.id === p.split("/")[1] && x.companyId === c.id);
    if (!target) return fail(404, "not_found", "That person was not found.");
    Object.assign(target, { role: b.role as Role });
    log(c, me, "people.role");
    return ok(personRow(target));
  }
  if (method === "PATCH" && p.startsWith("people/")) {
    if (!gate("people.update")) return NOT_ALLOWED();
    const target = people.find((x) => x.id === p.split("/")[1] && x.companyId === c.id);
    if (!target) return fail(404, "not_found", "That person was not found.");
    const { showsMyPay: myPay, ...rest } = b;
    if (typeof myPay === "boolean") setShowsMyPay(target.id, myPay);
    Object.assign(target, rest);
    log(c, me, "people.update");
    return ok(personRow(target));
  }
  if (route === "POST invites") {
    if (!gate("people.invite")) return NOT_ALLOWED();
    const r = b.role as Role;
    if (!canGiveRole(role, r)) return NOT_ALLOWED();
    if (b.phone && TWO_STEP_ROLES.includes(r))
      return fail(400, "invite_by_email", "Owners and managers are invited by email, since they sign in with a password.");
    const contact = str(b.email) || str(b.phone);
    if (!str(b.firstName).trim()) return fail(400, "bad_request", "Check firstName. Type their first name.");
    if (b.email ? !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact) : contact.replace(/\D/g, "").length < 10) {
      return fail(
        400,
        "bad_request",
        b.email ? "Check email. Type a full email address." : "Type the full phone number, with the area code.",
      );
    }
    const np = person(c, null, str(b.firstName), str(b.lastName), r === "franchise_owner" ? "owner" : r, {
      locale: str(b.locale, "en"),
    });
    people.push(np);
    msg(
      c,
      { channel: b.email ? "email" : "sms", to: contact, template: "invite", status: "dry_run", body: "Invite link (hidden)" },
      now(),
    );
    log(c, me, "people.invite");
    return ok(
      {
        id: id(),
        role: r,
        email: b.email ?? null,
        phone: b.phone ?? null,
        expiresAt: new Date(Date.now() + 7 * 864e5).toISOString(),
        personId: np.id,
      },
      201,
    );
  }
  // The preview has no Google Maps key, so its maps say one is needed.
  if (route === "GET maps-key") return gate("schedule.read") ? ok({ key: null }) : NOT_ALLOWED();
  if (route === "GET keys")
    return gate("keys.read")
      ? ok(
          keys
            .filter((k) => k.companyId === c.id && keyProvidersAt(companyLevel(chain(c))).includes(k.provider as KeyProvider))
            .map((k) => ({ provider: k.provider, saved: true, preview: k.preview, createdAt: k.createdAt })),
        )
      : NOT_ALLOWED();
  if (p.startsWith("keys/")) {
    if (!gate("keys.update")) return NOT_ALLOWED();
    const provider = p.split("/")[1] ?? "";
    const i = keys.findIndex((k) => k.companyId === c.id && k.provider === provider);
    if (method === "DELETE") {
      if (i < 0) return fail(404, "not_found", "That key was not found.");
      keys.splice(i, 1);
      log(c, me, "keys.remove");
      return ok({ ok: true });
    }
    const secret = str(b.secret);
    // Same rule as the server: a brand or location keeps only its Stripe keys; HQ runs the rest for everyone.
    if (!keyProvidersAt(companyLevel(chain(c))).includes(provider as KeyProvider))
      return fail(403, "hq_key", "Head office sets this key for every company. Ask head office if it needs to change.");
    if (secret.length < 8) return fail(400, "bad_request", "Check secret. Paste the whole key.");
    if (i >= 0) keys.splice(i, 1);
    const k = { companyId: c.id, provider, preview: `${secret.slice(0, 4)}••••`, createdAt: now() };
    keys.push(k);
    log(c, me, "keys.save");
    return ok({ provider, saved: true, preview: k.preview, createdAt: k.createdAt });
  }
  if (route === "GET audit")
    return gate("audit.read")
      ? ok({ items: audit.filter((a) => a.companyId === c.id).map(({ companyId: _c, ...a }) => a), next: null })
      : NOT_ALLOWED();
  if (route === "GET outbox")
    return gate("outbox.read")
      ? ok({ items: outbox.filter((m) => m.companyId === c.id).map(({ companyId: _c, ...m }) => m), next: null })
      : NOT_ALLOWED();
  if (route === "GET reports/companies") {
    if (!gate("report.children")) return NOT_ALLOWED();
    const list = [c, ...below(c.id)];
    return ok(
      list
        .map((x) => ({ companyId: x.id, name: x.name, parentId: x.parentId, ...workCounts(x.id) }))
        .sort((a, z) => a.name.localeCompare(z.name)),
    );
  }
  if (route === "GET home") return gate("schedule.read") ? ok(homeAnswer(c.id)) : NOT_ALLOWED();
  const payroll = payrollRoute(
    {
      companyId: c.id,
      role,
      gate,
      people: people.filter((x) => x.companyId === c.id),
      personId: people.find((x) => x.companyId === c.id && x.userId === me.id)?.id ?? null,
      ...resolveSettings(chain(c).map((x) => x.settings)).payroll,
      log: (a) => log(c, me, a),
    },
    method,
    p,
    url.searchParams,
    body,
  );
  if (payroll) return payroll;
  const prop = proposalsRoute(moneyCtx(c, role, me), method, p, body, resolveSettings(chain(c).map((x) => x.settings)).proposals);
  if (prop) return prop;
  const money = moneyRoute(moneyCtx(c, role, me), method, p, url.searchParams, body);
  if (money) return money;
  const [first = "", ...rest] = me.name.split(" ");
  const work = workRoute(
    { companyId: c.id, me: { id: me.id, name: shortName(first, rest.join(" ")) }, role, gate, people, log: (a) => log(c, me, a) },
    method,
    p,
    url.searchParams,
    body,
  );
  if (work) return work;
  return fail(404, "not_found", "That page or record was not found.");
}
